/*!
 * metro.js — metro(): a visual metronome, its click, and its beats as OSC
 * -------------------------------------------------------------------
 * Each client runs its own, from its element's score time on the shared
 * clock (serverClock.js), so the clients' metros agree.
 */

import { sharedAudioCtx, masterOutput } from "./audio/audioShared.js";
import { toBus } from "./audio/speakers.js";
import { sendOSC } from "../system/oscillaOSCClient.js";
import { scoreTimeToLocalWall, serverNow as clockServerNow, debugInfo as clockDebugInfo } from "../system/serverClock.js";
import { publish } from "../control/paramBinding.js";
import { scoreTimeAtWorldX } from "../system/scoreTime.js";
import { worldLeftX } from "../system/worldCoords.js";
import { secs } from "./beats.js";
import { onReset } from "../system/teardown.js";
import { transport } from "../system/transportState.js";

// Throttled metronome OSC sender
const _metroThrottleMap = new Map();
function sendMetronomeOsc(uid, beat, bpm) {
  const cleanUid = String(uid).replace(/^uid:/, "") || "default";
  const now = Date.now();
  if (_metroThrottleMap.has(cleanUid) && now - _metroThrottleMap.get(cleanUid) < 50) return;
  _metroThrottleMap.set(cleanUid, now);

  sendOSC({
    type: "oscilla/metro",
    uid: cleanUid,
    beat,
    bpm,
    client: window.localClientName || "unnamed",
    timestamp: now
  });
}

/* -------------------------------------------------------------------- */
/* Metro registry — one running metronome per uid                        */
/* -------------------------------------------------------------------- */
// A retrigger (playhead re-cross, click, rewind + replay) restarts the uid's
// metro rather than stacking a second timing loop (doubled flashes, doubled
// OSC), and transport/cleanup can stop them all.
const _metros = new Map();   // uid → stop()

export function stopMetro(uid) {
  const key = String(uid);
  const stop = _metros.get(key);
  if (!stop) return false;
  _metros.delete(key);
  try { stop(); } catch { /* ignore */ }
  return true;
}

export function stopAllMetros() {
  for (const uid of [..._metros.keys()]) stopMetro(uid);
}
// their clocks, and the overlays with them
onReset("project", function removeMetros() {
  stopAllMetros();
  document.querySelectorAll(".cue-metronome").forEach(el => el.remove());
});

// UID → deterministic click frequency (each metro has its own pitch)
function uidToFreq(id) {
  if (!id) return 440;
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = id.charCodeAt(i) + ((hash << 5) - hash);
  return 300 + (Math.abs(hash) % 400);
}

/* -------------------------------------------------------------------- */
/* Beat clock — lookahead scheduler, not requestAnimationFrame           */
/* -------------------------------------------------------------------- */
// Not requestAnimationFrame: there beats could only land on a frame, would
// stop in a background tab, and on return fire every missed beat on
// consecutive frames. A 25 ms interval schedules every beat falling in
// the next 120 ms: audio clicks at their exact time on the shared
// AudioContext clock, visual + OSC via a setTimeout to the beat. If the
// tab was throttled and we fell more than two beats behind, resync to now
// instead of catching up.
// originWall: wall-clock time of beat 1 of the grid. When it is derived
// from the cue's SCORE time (see handleMetronomeCue) every client shares
// the same grid, so metros click in phase across the network, late joiners
// land on the beat, and retriggers/rewinds re-derive the same phase.
function startBeatClock({ bpm, beats, onBeat, click, originWall = null, uid = null }) {
  const interval = 60000 / bpm;
  const LOOKAHEAD_MS = 120;
  const origin = Number.isFinite(originWall) ? originWall : performance.now() + 20;
  // first upcoming beat on the grid (never in the past)
  const snap = (t) => origin + Math.ceil((t - origin) / interval) * interval;
  let nextBeat = snap(performance.now() + 20);
  let stopped = false;
  const pending = new Set();

  const tick = () => {
    if (stopped) return;
    const now = performance.now();

    // The pulse as a signal. tick runs every 25 ms, so phase is continuous
    // enough to turn a rotation in time with the beat, and publish() throttles
    // it to one message per 16 ms anyway.
    if (uid && now >= origin) {
      const elapsed = (now - origin) / interval;      // beats since the grid origin
      publish("metro", uid, {
        beat:  (Math.floor(elapsed) % beats) + 1,     // 1..beats, the musical fact
        phase: (elapsed / beats) % 1,                 // through the bar, 0-1
        bar:   Math.floor(elapsed / beats),
      });
    }

    if (now - nextBeat > interval * 2) nextBeat = snap(now + 20);   // resync after throttle, on the grid
    while (nextBeat < now + LOOKAHEAD_MS) {
      const at = nextBeat;
      const k = Math.round((at - origin) / interval);           // beat index on the shared grid
      const n = ((k % beats) + beats) % beats + 1;
      if (click) click(at, n);
      const id = setTimeout(() => {
        pending.delete(id);
        if (!stopped) onBeat(n, at);
      }, Math.max(0, at - performance.now()));
      pending.add(id);
      nextBeat += interval;
    }
  };

  tick();
  const timer = setInterval(tick, 25);
  return () => {
    stopped = true;
    clearInterval(timer);
    for (const id of pending) clearTimeout(id);
    pending.clear();
  };
}

// Click on the SHARED AudioContext, scheduled at the beat's exact audio
// time. Never spawns a context of its own: one created without user
// activation stays suspended.
function makeClick(uid) {
  const ctx = sharedAudioCtx;
  const base = uidToFreq(uid);
  return (atWall, beat) => {
    if (!ctx || ctx.state !== "running") return;
    try {
      const when = ctx.currentTime + Math.max(0, (atWall - performance.now()) / 1000);
      const osc = ctx.createOscillator();
      const g = toBus(ctx);   // a gain, and both sides in a wider chain (speakers.js)
      osc.type = "sine";
      osc.frequency.setValueAtTime(beat === 1 ? base * 2 : base, when);
      g.gain.setValueAtTime(0.0001, when);
      g.gain.exponentialRampToValueAtTime(0.08, when + 0.003);
      g.gain.exponentialRampToValueAtTime(0.0001, when + 0.05);
      osc.connect(g);
      g.connect(masterOutput);
      osc.start(when);
      osc.stop(when + 0.06);
    } catch { /* ignore */ }
  };
}

/* -------------------------------------------------------------------- */
/* handleMetronomeCue                                                    */
/* -------------------------------------------------------------------- */
export function handleMetronomeCue(ast, cueElement = null) {
  const params = {};
  for (const p of (ast.args || [])) params[p.type] = p.value;

  const bpm = Number(params.bpm) > 0 ? Number(params.bpm) : 120;
  const beats = Math.max(1, Number(params.beats) || 4);
  const visual = String(params.visual || "circle").toLowerCase();
  const audioEnabled = Number(params.audio || 0) === 1;
  const positionMode = String(params.position || "fixed").toLowerCase();
  const oscEnabled = Number(params.osc || 0) === 1;
  const uid = String(params.uid || "default");
  const showCount = params.showcount !== "0" && params.showcount !== 0;
  const targetUid = params.target || null;
  // hideTarget: is the documented spelling, hideTrigger: the historical one
  const hideTrigger = Number(params.hideTrigger ?? params.hideTarget ?? 1) === 1;
  // hold: beats of tempo() (not of this metro's own bpm) -> seconds
  const holdSeconds = Number(secs(params.hold || 0));
  const colour = params.colour || params.color || "red";
  const size = Number(params.size || 50);

  // One metro per uid: a retrigger restarts it rather than stacking a loop
  stopMetro(uid);

  let targetEl = null;
  if (targetUid) {
    targetEl = document.querySelector(`[id='${targetUid}'], [data-uid='${targetUid}']`);
    if (!targetEl) console.warn(`[cue:metro] target '${targetUid}' not found — using cue element`);
  }

  const score = document.getElementById("scoreContainer");
  if (!score) return console.warn("[cue:metro] No score container found.");
  const anchorEl = targetEl || cueElement;
  if (!anchorEl) return console.warn("[cue:metro] No anchor element found.");

  if (visual === "self" && hideTrigger) {
    console.warn("[cue:metro] hideTrigger ignored because visual:self requires the trigger to remain visible.");
  }
  if (hideTrigger && visual !== "self" && cueElement) {
    cueElement.style.opacity = "0";
    cueElement.style.pointerEvents = "none";
    cueElement.style.visibility = "hidden";
  }

  const click = audioEnabled ? makeClick(uid) : null;
  // mute / monitor / throttle are handled inside sendOSC (oscillaOSCClient.js)
  const emitOsc = (n) => { if (oscEnabled) sendMetronomeOsc(uid, n, bpm); };

  // Beat-grid origin = the cue's SCORE time (where its element sits on the
  // timeline), mapped to this client's wall clock through the synced
  // transport. Same origin on every client → same phase. Falls back to the
  // local trigger moment when the cue has no timeline position.
  let originWall = null;
  let originSource = "local trigger moment (free-running)";
  // Where this metro sits in the score, in WORLD units. cue.triggerX is a
  // screen measurement — rect.left plus the stage's own offset on this
  // viewport — so it describes a different score time on every machine.
  // homeWorldX is the same
  // element measured in the units the playhead is measured in.
  const rec = cueElement && window.cues?.find?.(c => c.element === cueElement);
  let anchorX = Number.isFinite(rec?.homeWorldX) ? rec.homeWorldX : null;
  if (anchorX == null && cueElement) {
    // A cue registered at runtime (the DSL editor) has no homeWorldX — measure
    // it the same way. getBBox throws on anything that is not SVG geometry.
    try { anchorX = worldLeftX(cueElement); } catch { anchorX = null; }
  }
  const anchorMs = scoreTimeAtWorldX(anchorX, transport.scoreWidth, transport.duration);
  if (anchorMs != null) {
    // Preferred: the server's transport clock (serverClock.js). Every client
    // maps the same score time to the same instant, to within the ping/pong
    // offset error — a few ms on a LAN — regardless of how far its own
    // playhead has drifted. Fallback: this client's playhead position.
    const viaServer = scoreTimeToLocalWall(anchorMs);
    if (viaServer != null) {
      originWall = viaServer;
      originSource = "server transport clock";
    } else if (Number.isFinite(transport.elapsedTime)) {
      originWall = performance.now() - (transport.elapsedTime - anchorMs);
      originSource = "local playhead";
    }
  }
  {
    // Compare this line on two machines: the same server-time origin means
    // the same grid, whatever the local clock offsets are.
    const info = clockDebugInfo();
    const originServer = originWall != null && clockServerNow() != null
      ? Math.round(originWall - performance.now() + clockServerNow()) : null;
    console.log(`[cue:metro] ${uid} grid origin from ${originSource}` +
      (originServer != null ? ` — server time ${originServer} ms` : "") +
      (info.ready ? ` (clock offset ${info.offsetMs} ms, rtt ${info.rttMs} ms)` : " (server clock not ready)"));
  }

  let stopClock = null;
  let cleanupVisual = () => {};

  // ====================================================================
  // VISUAL:SELF — flash the target element (or the cue element)
  // ====================================================================
  if (visual === "self") {
    const flashEl = targetEl || cueElement;
    if (!flashEl) return console.warn("[cue:metro] visual:self but no element to flash");

    if (!flashEl.dataset._metroOrigFill) {
      flashEl.dataset._metroOrigFill = flashEl.getAttribute("fill") || window.getComputedStyle(flashEl).fill || "#000";
    }
    if (!flashEl.dataset._metroOrigStroke) {
      flashEl.dataset._metroOrigStroke = flashEl.getAttribute("stroke") || window.getComputedStyle(flashEl).stroke || "none";
    }
    const origFill = flashEl.dataset._metroOrigFill;
    const origStroke = flashEl.dataset._metroOrigStroke;

    const invertColor = (col) => {
      if (!col || col === "none") return null;
      if (col === "black") return "white";
      if (col === "white") return "black";
      if (/^#?[0-9A-Fa-f]{6}$/.test(col)) {
        const hex = col.replace("#", "");
        return `rgb(${255 - parseInt(hex.slice(0, 2), 16)},${255 - parseInt(hex.slice(2, 4), 16)},${255 - parseInt(hex.slice(4, 6), 16)})`;
      }
      return "#fff";
    };
    const invFill = invertColor(origFill) || origFill;
    const invStroke = invertColor(origStroke) || origStroke;

    let flashTimer = null;
    const restore = () => {
      flashEl.setAttribute("fill", origFill);
      if (origStroke !== "none") flashEl.setAttribute("stroke", origStroke);
      flashEl.style.opacity = "1.0";
    };
    const onBeat = (n) => {
      flashEl.setAttribute("fill", invFill);
      if (origStroke !== "none") flashEl.setAttribute("stroke", invStroke);
      flashEl.style.transition = "opacity 80ms ease";
      flashEl.style.opacity = "0.35";
      clearTimeout(flashTimer);
      flashTimer = setTimeout(restore, 120);
      emitOsc(n);
    };
    cleanupVisual = () => { clearTimeout(flashTimer); restore(); };
    stopClock = startBeatClock({ bpm, beats, onBeat, click, originWall, uid });
  }

  // ====================================================================
  // OVERLAY METRONOME (circle / square / diamond / triangle / hex)
  // ====================================================================
  else {
    const divId = `cue-metro-${uid}`;
    document.getElementById(divId)?.remove();   // stale overlay from a previous run

    const div = document.createElement("div");
    div.id = divId;
    div.className = "cue-metronome";
    Object.assign(div.style, {
      width: `${size}px`, height: `${size}px`,
      display: "flex", alignItems: "center", justifyContent: "center",
      color: "white", fontSize: `${size / 2.5}px`, textAlign: "center",
      opacity: "0.8", background: colour, zIndex: "99999",
      transition: "opacity 300ms ease, transform 100ms ease",
      pointerEvents: "none",
    });

    // Shape. The pulse re-applies baseTransform, so a diamond keeps its
    // rotation on every beat.
    let baseTransform = "translate(-50%, -50%)";
    switch (visual) {
      case "square":   div.style.borderRadius = "0"; break;
      case "diamond":  div.style.borderRadius = "0"; baseTransform += " rotate(45deg)"; break;
      case "hex":      div.style.borderRadius = "0";
                       div.style.clipPath = "polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)"; break;
      case "triangle":
        Object.assign(div.style, {
          width: "0", height: "0", borderRadius: "0", background: "transparent",
          borderLeft: `${size / 2}px solid transparent`,
          borderRight: `${size / 2}px solid transparent`,
          borderBottom: `${size}px solid ${colour}`,
        });
        break;
      default:         div.style.borderRadius = "50%";
    }
    div.style.transform = baseTransform;
    if (visual === "none") div.style.display = "none";     // the click and the OSC only

    // Placement. scrolling: inside the transformed #scrollStage in
    // stage-local coordinates, so it rides the score's scroll and zoom
    // (scrollLeft would not: the translate3d scroller never changes it).
    // fixed: pinned to the viewport at the anchor's position.
    const b = anchorEl.getBoundingClientRect();
    const stage = document.getElementById("scrollStage");
    if (positionMode === "scrolling" && stage) {
      const sb = stage.getBoundingClientRect();
      const sx = stage.offsetWidth ? (sb.width / stage.offsetWidth || 1) : 1;
      const sy = stage.offsetHeight ? (sb.height / stage.offsetHeight || 1) : 1;
      div.style.position = "absolute";
      div.style.left = `${(b.left + b.width / 2 - sb.left) / sx}px`;
      div.style.top = `${(b.top - sb.top) / sy - 10}px`;
      stage.appendChild(div);
    } else {
      div.style.position = "fixed";
      div.style.left = `${b.left + b.width / 2}px`;
      div.style.top = `${b.top - 10}px`;
      (document.getElementById("pageOverlayScore") || document.body).appendChild(div);
    }

    let pulseTimer = null;
    const onBeat = (n) => {
      if (visual !== "triangle") {
        div.style.transform = `${baseTransform} scale(1.3)`;
        clearTimeout(pulseTimer);
        pulseTimer = setTimeout(() => { div.style.transform = baseTransform; }, 100);
        div.textContent = showCount ? String(n) : "";
      }
      emitOsc(n);
    };
    cleanupVisual = () => {
      clearTimeout(pulseTimer);
      div.style.opacity = "0";
      setTimeout(() => div.remove(), 300);
    };
    stopClock = startBeatClock({ bpm, beats, onBeat, click, originWall, uid });
  }

  let holdTimer = null;
  const stop = () => {
    clearTimeout(holdTimer);
    stopClock?.();
    cleanupVisual();
  };
  _metros.set(uid, stop);
  // the hold ends this run, never a later run of the same uid (which stops
  // this one first, and its timer with it)
  if (holdSeconds > 0) holdTimer = setTimeout(() => { if (_metros.get(uid) === stop) stopMetro(uid); }, holdSeconds * 1000);
}

// Starts the trig:auto metronomes among cues (the score's, by default),
// once each.
export function autostartMetronomes(cues = window.cues) {
  if (!cues) return;

  for (const c of cues) {
    if (c.ast?.type !== "cueMetronome" && c.ast?.type !== "cueMetro")
      continue;

    const trigPair = c.ast.args?.find(p => p.type === "trig");
    const trig = (trigPair?.value || "manual").toLowerCase();
    if (trig !== "auto") continue;

    if (c._autoStarted) continue;
    c._autoStarted = true;

    handleMetronomeCue(c.ast, c.element);
  }
}
